import {
  Button,
  Card,
  Form,
  FormError,
  FormRow,
  FormRowItem,
  MaterialAutocompleteInput,
  OutlinedContainer,
  TextInput,
  TimeInput,
} from "@tphglobal/components";
import React, { useEffect, useRef, useState } from "react";
import {
  brand,
  brandColour,
  greyScaleColour,
  otherColour,
} from "@tphglobal/common/theme/style.palette";
import moment from "moment";
import { useFormReducer } from "@tphglobal/common/hooks";
import { FixedTitle, mapFlagToValue, required } from "@tphglobal/common/utils";
import { useSelector } from "react-redux";
import { fontSize, fontWeight } from "@tphglobal/common/theme/style.typography";
import { Accordion, Grid } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomCheckbox from "@tphglobal/components/checkBoxInput";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DateInput from "@tphglobal/components/materialDateInput";
import {
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledHeading,
} from "../styles";
import {
  formatTime,
  getDefaultWeekDay,
  joinDelimitedValues,
  parseDelimitedValues,
  toCamelCase,
} from "../../../utils/commonFunctions";
import {
  CompanyDetailedEntity,
  CustomField,
  DailyTime,
  DropListItem,
  Payas,
  SetLockedRows,
  TimeOption,
  UnitsByRangeDate,
  Validators,
} from "../../../models";
import CustomFieldComponent from "../customFieldComponent";
import messages from "../../../messages";
import { Container } from "../../../components";
import { ReduxState } from "../../../redux/reducers";
import timeInputField from "../tableInputs/timeInputField";
import MultiSelectInput from "../tableInputs/multiSelectInput";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DEFAULT_UNIT_FIELDS } from "../../../utils/constant";
import { getValidSelectedLocations } from "../../../utils/timeCardFunctions";

interface Screens {
  screen1: boolean;
  screen2: boolean;
  screen3: boolean;
}

interface FormData {
  StartMeal: Date;
  StartMeal2: Date;
  StartMeal3: Date;
  FinishMeal1: Date;
  FinishMeal3: Date;
  FinishMeal2: Date;
  MB1ND: boolean;
  MB2ND: boolean;
  MB3ND: boolean;
  Start: Date;
  Finish: Date;
  Payas: { id: string; label: string };
  Travel1: string;
  Note: string | null;
  Travel2: string;
  TotalMBDed: string;
  Unit: string | null;
  Locations: string | null;
  CamCall: Date;
  CamWrap: Date;
}

interface Props {
  setScreenIndex: (screenIndex: Screens) => void;
  passedData: DailyTime;
  companyDetails: CompanyDetailedEntity;
  setWeekData: (update: (weekData: DailyTime[]) => DailyTime[]) => void;
  rowIndex: number | null;
  weekData: DailyTime[];
  unitsByRange: UnitsByRangeDate[];
  payAsLockedRows: number[];
  setPayAsLockedRows?: SetLockedRows;
}

let validators: Validators = {
  Payas: [required(messages?.general?.validations?.payAsRequired)],
};
const iconStyles = {
  height: fontSize?.h4,
  width: fontSize?.h4,
  color: brandColour?.primary100,
};
const expandIconStyles = {
  height: fontSize?.h3,
  width: fontSize?.h3,
  fill: brand?.secondaryMain,
};

const DATE_FORMAT = "DD/MM/YYYY";

const AddTimeCardForm: React.FC<Props> = ({
  setScreenIndex,
  passedData,
  companyDetails,
  setWeekData,
  rowIndex,
  weekData,
  unitsByRange,
  payAsLockedRows,
  setPayAsLockedRows,
}) => {
  const {
    submitting,
    submitError,
    handleSubmit,
    connectField,
    change,
    setSubmitError,
    formValues,
  } = useFormReducer(validators);
  const isFirstRun = useRef(true);

  const userProfile = useSelector((state: ReduxState) => state?.profile);
  const [useMBValues, setUseMBValues] = useState<boolean>(false);
  const isLocked = companyDetails?.allowMealBreak || false;

  const [yesterdayTimings, setYesterdayTimings] = useState<DailyTime>();
  const [locations, setLocations] = useState<DropListItem[]>([]);

  const date = weekData?.[rowIndex || 0]?.Date;
  const selectedUnitByDate = unitsByRange?.find((u) => u.date === date);
  const unitsForDate =
    selectedUnitByDate?.units?.map((u, i) => ({
      value: u.unit,
      label: u.unit,
      id: i + 1,
    })) || [];
  const selectedUnit = weekData?.[rowIndex || 0]?.Unit;
  const selectedLocations = weekData?.[rowIndex || 0]?.Locations;
  const locationsForUnit =
    unitsByRange
      ?.find((u) => u.date === date)
      ?.units?.find((u) => u.unit === selectedUnit)
      ?.locations.map((loc, i) => ({ value: loc, label: loc, id: i })) || [];
  const unitListWithEmpty =
    unitsForDate.length > 0
      ? [{ value: null, label: "Select", id: 0 }, ...unitsForDate]
      : [];
  const getTotalHours = () => {
    if (formValues?.Payas?.value?.id === "DNW") {
      return "00:00";
    }
    const startMB1 = moment(formValues?.StartMeal?.value, "HH:mm");
    const startMB2 = moment(formValues?.StartMeal2?.value, "HH:mm");
    const startMB3 = moment(formValues?.StartMeal3?.value, "HH:mm");
    const finishMB1 = moment(formValues?.FinishMeal1?.value, "HH:mm");
    const finishMB2 = moment(formValues?.FinishMeal2?.value, "HH:mm");
    const finishMB3 = moment(formValues?.FinishMeal3?.value, "HH:mm");

    const startWorkTime = moment(formValues?.Start?.value, "HH:mm");
    const finishWorkTime = moment(formValues?.Finish?.value, "HH:mm");
    const travelToTime = moment(formValues?.Travel1?.value, "HH:mm");
    const travelFromTime = moment(formValues?.Travel2?.value, "HH:mm");
    const defaultMealBreakTime = moment(formValues?.TotalMBDed?.value, "HH:mm");

    const calculateMealBreakDuration = () => {
      const totalMealBreakDuration = moment.duration(0);

      // Clone moments to avoid mutating originals
      const startMB1Copy = startMB1.clone();
      const finishMB1Copy = finishMB1.clone();
      const startMB2Copy = startMB2.clone();
      const finishMB2Copy = finishMB2.clone();
      const startMB3Copy = startMB3.clone();
      const finishMB3Copy = finishMB3.clone();

      // Handle midnight crossover for each meal break
      if (
        finishMB1Copy.isValid() &&
        startMB1Copy.isValid() &&
        finishMB1Copy.isBefore(startMB1Copy)
      ) {
        finishMB1Copy.add(1, "day");
      }
      if (
        finishMB2Copy.isValid() &&
        startMB2Copy.isValid() &&
        finishMB2Copy.isBefore(startMB2Copy)
      ) {
        finishMB2Copy.add(1, "day");
      }
      if (
        finishMB3Copy.isValid() &&
        startMB3Copy.isValid() &&
        finishMB3Copy.isBefore(startMB3Copy)
      ) {
        finishMB3Copy.add(1, "day");
      }

      // Add meal break durations using the corrected times
      if (
        startMB1Copy.isValid() &&
        finishMB1Copy.isValid() &&
        finishMB1Copy.isAfter(startMB1Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB1Copy.diff(startMB1Copy)),
        );
      }
      if (
        startMB2Copy.isValid() &&
        finishMB2Copy.isValid() &&
        finishMB2Copy.isAfter(startMB2Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB2Copy.diff(startMB2Copy)),
        );
      }
      if (
        startMB3Copy.isValid() &&
        finishMB3Copy.isValid() &&
        finishMB3Copy.isAfter(startMB3Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB3Copy.diff(startMB3Copy)),
        );
      }

      // Subtract non-deductible meal breaks
      if (totalMealBreakDuration.asMilliseconds() > 0) {
        if (formValues?.MB1ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB1Copy.diff(startMB1Copy)),
          );
        }
        if (formValues?.MB2ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB2Copy.diff(startMB2Copy)),
          );
        }
        if (formValues?.MB3ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB3Copy.diff(startMB3Copy)),
          );
        }

        return totalMealBreakDuration;
      }
      return moment.duration(
        defaultMealBreakTime.diff(moment().startOf("day")),
      );
    };

    const mealBreakDuration = calculateMealBreakDuration();

    if (startWorkTime.isValid() && finishWorkTime.isValid()) {
      if (finishWorkTime.isBefore(startWorkTime)) {
        finishWorkTime.add(1, "day");
      }

      const totalWorkDuration = moment.duration(
        finishWorkTime.diff(startWorkTime),
      );

      let netDuration = moment.duration(0);

      if (
        mealBreakDuration.asMilliseconds() < totalWorkDuration.asMilliseconds()
      ) {
        netDuration = totalWorkDuration.subtract(mealBreakDuration);
      }

      if (!companyDetails?.TravelToHide) {
        const travelToDuration = moment.duration(
          travelToTime.diff(moment().startOf("day")),
        );
        netDuration = netDuration.add(travelToDuration);
      }

      if (!companyDetails?.TravelFromHide) {
        const travelFromDuration = moment.duration(
          travelFromTime.diff(moment().startOf("day")),
        );
        netDuration = netDuration.add(travelFromDuration);
      }

      const netDurationMoment = moment.duration(netDuration.asMilliseconds());
      const totalHours = `${Math.floor(netDurationMoment.asHours())
        .toString()
        .padStart(2, "0")}:${netDurationMoment
        .minutes()
        .toString()
        .padStart(2, "0")}`;

      return totalHours;
    }

    return "00:00";
  };

  const setFormValues = () => {
    change(
      "Payas",
      passedData?.Payas !== "0" && {
        id: companyDetails?.Payas?.find(
          (opt: Payas) => opt?.Value === passedData?.Payas,
        )?.Flag,
        label: passedData?.Payas,
      },
    );
    change("Start", moment(passedData?.Start, "HH:mm"));
    change("TotalMBDed", moment(passedData?.TotalMBDed, "HH:mm"));
    change("Finish", moment(passedData?.Finish, "HH:mm"));
    change("StartMeal", moment(passedData?.StartMeal, "HH:mm"));
    change("StartMeal2", moment(passedData?.StartMeal2, "HH:mm"));
    change("StartMeal3", moment(passedData?.StartMeal3, "HH:mm"));
    change("FinishMeal1", moment(passedData?.FinishMeal1, "HH:mm"));
    change("FinishMeal2", moment(passedData?.FinishMeal2, "HH:mm"));
    change("FinishMeal3", moment(passedData?.FinishMeal3, "HH:mm"));
    change("Travel1", moment(passedData?.Travel1, "HH:mm"));
    change("Travel2", moment(passedData?.Travel2, "HH:mm"));
    change("MB1ND", passedData?.MB1ND === "1");
    change("MB2ND", passedData?.MB2ND === "1");
    change("MB3ND", passedData?.MB3ND === "1");
    change("MB3ND", passedData?.MB3ND === "1");
    change("Date", moment(passedData?.Date, DATE_FORMAT));

    let userNote = passedData?.customNote || "";
    const payAsNote = passedData?.Payas || "";

    if (userNote && payAsNote && userNote.startsWith(payAsNote)) {
      userNote = userNote.slice(payAsNote.length).trimStart();
    }
    change("Note", userNote);

    if (passedData?.Unit && setPayAsLockedRows) {
      setPayAsLockedRows((prev) => {
        const updated = [...prev];
        updated[rowIndex || 0] = passedData?.Payas !== "0" ? 1 : 0;
        return updated;
      });
    }

    if (passedData?.CustomTimeFields?.length > 0) {
      passedData?.CustomTimeFields.forEach((field: CustomField) => {
        change(toCamelCase(field?.Prompt), field?.Value);
      });
    }

    const newValidators: Validators = { ...validators };
    companyDetails?.CustomTimeFields?.forEach((field: CustomField) => {
      if (field?.required === 1) {
        newValidators[toCamelCase(field?.Prompt)] = [
          required("Field cannot be empty, please enter some value."),
        ];
      }
    });
    validators = newValidators;
  };

  useEffect(() => {
    setFormValues();
    change("Unit", passedData?.Unit || null);
    change("Locations", parseDelimitedValues(passedData?.Locations));
    change("CamCall", moment(passedData?.CamCall || "00:00", "HH:mm"));
    change("CamWrap", moment(passedData?.CamWrap || "00:00", "HH:mm"));
    setLocations(locationsForUnit);
  }, []);

  useEffect(() => {
    const selectedUnit =
      typeof formValues?.Unit?.value === "object"
        ? formValues?.Unit?.value?.value
        : formValues?.Unit?.value;

    if (selectedUnit) {
      const foundDate = unitsByRange?.find((u) => u.date === date);
      const foundUnit = foundDate?.units?.find((u) => u.unit === selectedUnit);
      const locations =
        foundUnit?.locations?.map((loc, i) => ({
          value: loc,
          label: loc,
          id: i,
        })) || [];
      setLocations(locations);
      const validSelectedLocations = getValidSelectedLocations(
        selectedLocations,
        locations,
      );

      change(
        "Locations",
        validSelectedLocations.length > 0
          ? validSelectedLocations
          : locations.length === 1
            ? [locations[0].value]
            : [],
      );

      change("CamCall", moment(foundUnit?.CamCall || "00:00", "HH:mm"));
      change("CamWrap", moment(foundUnit?.CamWrap || "00:00", "HH:mm"));

      if (isFirstRun.current) {
        setFormValues();
        isFirstRun.current = false;
        return;
      }
      if (foundUnit) {
        change(
          "Payas",
          foundUnit.Payas !== "0"
            ? {
                id: companyDetails?.Payas?.find(
                  (opt: Payas) => opt?.Value === foundUnit.Payas,
                )?.Flag,
                label: foundUnit.Payas,
              }
            : null,
        );
        change("Start", moment(foundUnit.Start, "HH:mm"));
        change("TotalMBDed", moment(foundUnit.TotalMBDed, "HH:mm"));
        change("Finish", moment(foundUnit.Finish, "HH:mm"));
        change("StartMeal", moment(foundUnit.StartMeal, "HH:mm"));
        change("StartMeal2", moment(foundUnit.StartMeal2, "HH:mm"));
        change("StartMeal3", moment(foundUnit.StartMeal3, "HH:mm"));
        change("FinishMeal1", moment(foundUnit.FinishMeal1, "HH:mm"));
        change("FinishMeal2", moment(foundUnit.FinishMeal2, "HH:mm"));
        change("FinishMeal3", moment(foundUnit.FinishMeal3, "HH:mm"));
        change("Travel1", moment(foundUnit.Travel1, "HH:mm"));
        change("Travel2", moment(foundUnit.Travel2, "HH:mm"));
        change("MB1ND", foundUnit.MB1ND === "1");
        change("MB2ND", foundUnit.MB2ND === "1");
        change("MB3ND", foundUnit.MB3ND === "1");

        let userNote = foundUnit.customNote || "";
        const payAsNote = foundUnit.Payas || "";

        if (userNote && payAsNote && userNote.startsWith(payAsNote)) {
          userNote = userNote.slice(payAsNote.length).trimStart();
        }
        change("Note", userNote);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = foundUnit.Payas !== "0" ? 1 : 0;
          return updated;
        });
      }
    } else {
      setLocations([]);
      change("Locations", []);
      if (showUnitSection) {
        if (isFirstRun.current && typeof formValues?.Unit === "object") {
          setFormValues();
          isFirstRun.current = false;
          return;
        }
        change("Payas", null);
        change("Start", moment(DEFAULT_UNIT_FIELDS.Start, "HH:mm"));
        change("TotalMBDed", moment(DEFAULT_UNIT_FIELDS.TotalMBDed, "HH:mm"));
        change("Finish", moment(DEFAULT_UNIT_FIELDS.Finish, "HH:mm"));
        change("StartMeal", moment(DEFAULT_UNIT_FIELDS.StartMeal, "HH:mm"));
        change("StartMeal2", moment(DEFAULT_UNIT_FIELDS.StartMeal2, "HH:mm"));
        change("StartMeal3", moment(DEFAULT_UNIT_FIELDS.StartMeal3, "HH:mm"));
        change("FinishMeal1", moment(DEFAULT_UNIT_FIELDS.FinishMeal1, "HH:mm"));
        change("FinishMeal2", moment(DEFAULT_UNIT_FIELDS.FinishMeal2, "HH:mm"));
        change("FinishMeal3", moment(DEFAULT_UNIT_FIELDS.FinishMeal3, "HH:mm"));
        change("Travel1", moment(DEFAULT_UNIT_FIELDS.Travel1, "HH:mm"));
        change("Travel2", moment(DEFAULT_UNIT_FIELDS.Travel2, "HH:mm"));
        change("MB1ND", DEFAULT_UNIT_FIELDS.MB1ND === "1");
        change("MB2ND", DEFAULT_UNIT_FIELDS.MB2ND === "1");
        change("MB3ND", DEFAULT_UNIT_FIELDS.MB3ND === "1");
        change("CamCall", moment(DEFAULT_UNIT_FIELDS.CamCall, "HH:mm"));
        change("CamWrap", moment(DEFAULT_UNIT_FIELDS.CamWrap, "HH:mm"));
        change("Note", DEFAULT_UNIT_FIELDS.Note);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = 0;
          return updated;
        });
      }
    }
  }, [formValues?.Unit]);

  const handleGoBack = () => {
    setScreenIndex({
      screen1: false,
      screen2: true,
      screen3: false,
    });
  };

  const onSubmit = async (data: FormData) => {
    const startMB1 = moment(data?.StartMeal || "00:00", "HH:mm");
    const startMB2 = moment(data?.StartMeal2 || "00:00", "HH:mm");
    const startMB3 = moment(data?.StartMeal3 || "00:00", "HH:mm");
    const finishMB1 = moment(data?.FinishMeal1 || "00:00", "HH:mm");
    const finishMB2 = moment(data?.FinishMeal2 || "00:00", "HH:mm");
    const finishMB3 = moment(data?.FinishMeal3 || "00:00", "HH:mm");
    const defaultMealBreakTime = moment(data?.TotalMBDed || "00:00", "HH:mm");

    const mealBreakTime = moment.duration(
      defaultMealBreakTime.diff(moment().startOf("day")),
    );
    const startWorkTime = moment(data?.Start || "00:00", "HH:mm");
    const finishWorkTime = moment(data?.Finish || "00:00", "HH:mm");
    const travelToTime = moment(data?.Travel1 || "00:00", "HH:mm");
    const travelFromTime = moment(data?.Travel2 || "00:00", "HH:mm");
    const totalHours = getTotalHours();
    if (
      startWorkTime.isValid() &&
      finishWorkTime.isValid() &&
      mealBreakTime.isValid()
    ) {
      if (finishWorkTime.isBefore(startWorkTime)) {
        finishWorkTime.add(1, "day");
      }

      const totalWorkDuration = moment.duration(
        finishWorkTime.diff(startWorkTime),
      );

      if (
        moment.duration(totalHours).asMinutes() >
        moment.duration(24, "hours").asMinutes()
      ) {
        setSubmitError("Your total daily hours cannot exceed 24 hours");
        return;
      }

      if (mealBreakTime.asMinutes() > totalWorkDuration.asMinutes()) {
        setSubmitError(
          "Meal break cannot be longer than the total work duration",
        );
        return;
      }
    } else {
      if (!startWorkTime?.isValid()) {
        setSubmitError("Invalid start work time");
      } else if (!finishWorkTime?.isValid()) {
        setSubmitError("Invalid finish work time");
      } else if (!mealBreakTime?.isValid()) {
        setSubmitError("Invalid meal break time");
      } else if (!travelToTime?.isValid() && companyDetails?.TravelToHide) {
        setSubmitError("Invalid travel to time");
      } else if (!travelFromTime?.isValid() && companyDetails?.TravelFromHide) {
        setSubmitError("Invalid travel from time");
      } else if (!startMB1?.isValid() || !finishMB1?.isValid()) {
        setSubmitError("Invalid start or finish meal break 1 time");
      } else if (!startMB2?.isValid() || !finishMB2?.isValid()) {
        setSubmitError("Invalid start or finish meal break 2 time");
      } else if (!startMB3?.isValid() || !finishMB3?.isValid()) {
        setSubmitError("Invalid start or finish meal break 3 time");
      }
      return;
    }
    setSubmitError("");

    const updatedWeekData: DailyTime = {
      ...passedData,
      CustomTimeFields:
        passedData?.CustomTimeFields?.map((field) => ({ ...field })) || [],
    };
    Object.keys(data).forEach((key: keyof FormData) => {
      if (key === "Unit") {
        const selectedUnit =
          typeof data[key] === "object"
            ? typeof data[key]?.value === "object"
              ? data[key]?.value?.value
              : data[key]?.value
            : data[key];
        updatedWeekData[key] = selectedUnit ?? null;
      } else if (key === "Locations") {
        updatedWeekData[key] = joinDelimitedValues(data[key]) || null;
      } else if (key === "Note") {
        updatedWeekData[key] = data[key];
      } else if (key === "Payas") {
        updatedWeekData[key] = data[key]?.label;
      } else if (key === "MB1ND" || key === "MB2ND" || key === "MB3ND") {
        updatedWeekData[key] = data[key] ? "1" : "0";
      } else {
        updatedWeekData[key] = moment(data[key]).format("HH:mm");
      }
    });

    (updatedWeekData?.CustomTimeFields || []).forEach(
      (timeField: CustomField, index: number) => {
        const key = toCamelCase(timeField?.Prompt);

        if (key in data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const value = (data as { [key: string]: any })[key];

          updatedWeekData.CustomTimeFields[index].Value =
            typeof value === "boolean"
              ? value
                ? "1"
                : "0"
              : typeof value === "object" && value !== null && "label" in value
                ? value.label
                : value;
        }
      },
    );
    updatedWeekData.customNote = `${data?.Payas?.label || data?.Payas} ${
      data?.Note || ""
    }`;
    updatedWeekData.TotalHours = getTotalHours();
    updatedWeekData.Date = passedData?.Date;
    if (updatedWeekData?.Unit && setPayAsLockedRows) {
      setPayAsLockedRows((prev) => {
        const updated = [...prev];
        updated[rowIndex || 0] = 1;
        return updated;
      });
    }
    setWeekData((weekData: DailyTime[]): DailyTime[] => {
      const newWeekData = [...weekData];
      newWeekData[rowIndex] = updatedWeekData;
      return newWeekData;
    });
    handleGoBack();
  };

  useEffect(() => {
    getTotalHours();
  }, [formValues?.Start, formValues?.Finish, formValues?.TotalMBDed]);

  const handleCopyYesterdayTimings = () => {
    const currDay: DailyTime = passedData;
    let lastDay: DailyTime;
    const index = rowIndex;
    if (index === 0) {
      lastDay = getDefaultWeekDay(passedData?.Date);
    } else {
      lastDay = {
        ...weekData[index - 1],
        Unit: currDay?.Unit,
        Locations: currDay?.Locations,
        CamCall: currDay?.CamCall,
        CamWrap: currDay?.CamWrap,
        Date: currDay?.Date,
        Day: currDay?.Day,
        Note: null,
      };
    }
    if (lastDay.Payas === "0") {
      setPayAsLockedRows?.((prev) => {
        const updated = [...prev];
        updated[rowIndex || 0] = 0;
        return updated;
      });
    }
    setYesterdayTimings(lastDay);
  };
  useEffect(() => {
    if (yesterdayTimings) {
      const copyMode = userProfile?.CopyYesterdayTimes;
      const isWorked = yesterdayTimings?.Payas?.toLowerCase() === "worked";
      let payAsValue = null;
      if (isWorked) {
        change("Payas", {
          id: companyDetails?.Payas?.find(
            (opt: Payas) => opt?.Value === "Worked",
          )?.Flag,
          label: "Worked",
        });
        payAsValue = "Worked";
      } else {
        // reset PayAs when yesterday PayAs is NOT Worked
        change("Payas", null);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = 0;
          return updated;
        });
      }
      change("Start", moment(yesterdayTimings?.Start, "HH:mm"));
      change("TotalMBDed", moment(yesterdayTimings?.TotalMBDed, "HH:mm"));
      change("Finish", moment(yesterdayTimings?.Finish, "HH:mm"));
      change("StartMeal", moment(yesterdayTimings?.StartMeal, "HH:mm"));
      change("StartMeal2", moment(yesterdayTimings?.StartMeal2, "HH:mm"));
      change("StartMeal3", moment(yesterdayTimings?.StartMeal3, "HH:mm"));
      change("FinishMeal1", moment(yesterdayTimings?.FinishMeal1, "HH:mm"));
      change("FinishMeal2", moment(yesterdayTimings?.FinishMeal2, "HH:mm"));
      change("FinishMeal3", moment(yesterdayTimings?.FinishMeal3, "HH:mm"));
      change("Travel1", moment(yesterdayTimings?.Travel1, "HH:mm"));
      change("Travel2", moment(yesterdayTimings?.Travel2, "HH:mm"));
      change("MB1ND", yesterdayTimings?.MB1ND === "1");
      change("MB2ND", yesterdayTimings?.MB2ND === "1");
      change("MB3ND", yesterdayTimings?.MB3ND === "1");
      change("Date", moment(yesterdayTimings?.Date, DATE_FORMAT));

      if (copyMode === "COPY_EVERYTHING") {
        if (payAsValue) {
          let userNote = yesterdayTimings?.customNote || "";

          if (userNote && payAsValue && userNote.startsWith(payAsValue)) {
            userNote = userNote.slice(payAsValue.length).trimStart();
          }
          change("Note", userNote);
        } else {
          change("Note", "");
        }

        // Copy CUSTOM FIELDS
        if (yesterdayTimings?.CustomTimeFields?.length > 0) {
          yesterdayTimings.CustomTimeFields.forEach((field: CustomField) => {
            const key = toCamelCase(field.Prompt);
            change(
              key,
              field.Value === "1"
                ? true
                : field.Value === "0"
                  ? false
                  : field.Value,
            );
          });
        }
      } else {
        change("Note", "");
      }
    }
  }, [yesterdayTimings]);

  useEffect(() => {
    const hasValidMealTime = [
      formValues?.StartMeal?.value,
      formValues?.StartMeal2?.value,
      formValues?.StartMeal3?.value,
      formValues?.FinishMeal1?.value,
      formValues?.FinishMeal2?.value,
      formValues?.FinishMeal3?.value,
    ].some((value) => value && moment(value).format("HH:mm") !== "00:00");

    if (hasValidMealTime) {
      const startMoment1 = moment(formValues?.StartMeal?.value);
      const finishMoment1 = moment(formValues?.FinishMeal1?.value);
      const startMoment2 = moment(formValues?.StartMeal2?.value);
      const finishMoment2 = moment(formValues?.FinishMeal2?.value);
      const startMoment3 = moment(formValues?.StartMeal3?.value);
      const finishMoment3 = moment(formValues?.FinishMeal3?.value);

      // Handle midnight crossover for each meal break
      if (
        finishMoment1.isValid() &&
        startMoment1.isValid() &&
        finishMoment1.isBefore(startMoment1)
      ) {
        finishMoment1.add(1, "day");
      }
      if (
        finishMoment2.isValid() &&
        startMoment2.isValid() &&
        finishMoment2.isBefore(startMoment2)
      ) {
        finishMoment2.add(1, "day");
      }
      if (
        finishMoment3.isValid() &&
        startMoment3.isValid() &&
        finishMoment3.isBefore(startMoment3)
      ) {
        finishMoment3.add(1, "day");
      }

      const diff1 = finishMoment1.diff(startMoment1);
      const diff2 = finishMoment2.diff(startMoment2);
      const diff3 = finishMoment3.diff(startMoment3);

      const totalMealTimeMili: number = [diff1, diff2, diff3].reduce(
        (total, diff) => (diff > 0 ? total + diff : total),
        0,
      );
      const formattedMealTime: string = formatTime(totalMealTimeMili);

      if (totalMealTimeMili >= 0) {
        change("TotalMBDed", moment(formattedMealTime, "HH:mm"));
        setUseMBValues(totalMealTimeMili !== 0);
      }
    } else {
      setUseMBValues(false);
    }
  }, [
    formValues?.StartMeal?.value,
    formValues?.StartMeal2?.value,
    formValues?.StartMeal3?.value,
    formValues?.FinishMeal1?.value,
    formValues?.FinishMeal2?.value,
    formValues?.FinishMeal3?.value,
  ]);

  useEffect(() => {
    const isDNW = formValues?.Payas?.value?.id === "DNW";

    if (isDNW) {
      const zeroTime = moment("00:00", "HH:mm");

      // Work & travel times
      change("Start", zeroTime);
      change("Finish", zeroTime);
      change("Travel1", zeroTime);
      change("Travel2", zeroTime);
      change("TotalMBDed", zeroTime);
    }
  }, [formValues?.Payas]);

  const userMinutesStep = userProfile?.MinutsStep || 5;
  const showCustomTimeField =
    userProfile?.TimeOption === TimeOption.Type ? timeInputField : undefined;

  const isDropdownDisabled = userProfile?.TimeOption === TimeOption.Type;
  const isDNW = formValues?.Payas?.value?.id === "DNW";
  const showUnitSection =
    unitsByRange?.length > 0 && unitListWithEmpty?.length > 0;
  const camCall = formValues?.CamCall?.value?.format?.("HH:mm");
  const camWrap = formValues?.CamWrap?.value?.format?.("HH:mm");
  const showCamCall = Boolean(
    formValues?.Unit?.value && camCall && camCall !== "00:00",
  );
  const showCamWrap = Boolean(
    formValues?.Unit?.value && camWrap && camWrap !== "00:00",
  );

  return (
    <Container
      showGoBack
      customGoBack={handleGoBack}
      heading={messages?.timeCard?.heading}
      background={otherColour?.adminBg100}
      maxHeight
    >
      <Card
        showGoBack
        noContentPadding
        removeTitle
        contentCss={{ padding: "0 16px 24px !important" }}
      >
        <FormRow
          display="flex"
          justifyContent="space-between"
          marginTop={3}
          marginBottom={2}
        >
          <StyledHeading
            fontSize="18px"
            fontWeight={fontWeight?.semiBold}
            lineHeight="23px"
          >
            {messages?.timeCard?.form?.subHeading}
          </StyledHeading>
          <FixedTitle
            varient="Fixed12Medium"
            color={brandColour?.primary100}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
            onClick={handleCopyYesterdayTimings}
          >
            <ContentCopyIcon
              style={{
                ...iconStyles,
                height: fontSize?.h5,
                width: fontSize?.h5,
              }}
            />
            {messages?.timeCard?.copyYesterday}
          </FixedTitle>
        </FormRow>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Grid container>
            <Grid
              container
              marginBottom="12px"
              columnSpacing={2}
              rowSpacing={2}
            >
              {showUnitSection && (
                <Grid item xs={12}>
                  {connectField("Unit", {
                    label: "Unit",
                    options: unitListWithEmpty,
                  })(MaterialAutocompleteInput)}
                </Grid>
              )}
              {showUnitSection &&
                formValues?.Unit?.value &&
                locations?.length > 0 && (
                  <Grid item xs={12}>
                    {connectField("Locations", {
                      fieldKey: "Locations",
                      rowIndex,
                      list: locations,
                      enablePortal: locations.length > 0,
                      onChange: (vals) => change("Locations", vals),
                      IconComponent: KeyboardArrowDownIcon,
                      value: formValues?.Locations?.value,
                      label: "Locations",
                    })(MultiSelectInput)}
                  </Grid>
                )}
              {showCamCall && (
                <Grid item xs={12}>
                  {connectField("CamCall", {
                    label: messages?.timeCard?.updateTime?.camCall,
                    disabled: true,
                    CustomTimeField: showCustomTimeField,
                    minuteSteps: userMinutesStep,
                    disableDropDown: true,
                  })(TimeInput)}
                </Grid>
              )}
              {showCamWrap && (
                <Grid item xs={12}>
                  {connectField("CamWrap", {
                    label: messages?.timeCard?.updateTime?.camWrap,
                    disabled: true,
                    CustomTimeField: showCustomTimeField,
                    minuteSteps: userMinutesStep,
                    disableDropDown: true,
                  })(TimeInput)}
                </Grid>
              )}

              <Grid item xs={12}>
                {connectField("Payas", {
                  label: messages?.timeCard?.updateTime?.payAs,
                  options: companyDetails?.Payas?.map(mapFlagToValue),
                  disabled:
                    rowIndex !== undefined && payAsLockedRows[rowIndex] === 1,
                })(MaterialAutocompleteInput)}
              </Grid>
              <Grid item xs={12}>
                {connectField("Date", {
                  label: messages?.timeCard?.updateTime?.date,
                  disabled: true,
                })(DateInput)}
              </Grid>
              <Grid item xs={12}>
                <StyledHeading fontWeight={fontWeight?.bold} lineHeight="18px">
                  {messages?.timeCard?.updateTime?.day}:{passedData?.Day}
                </StyledHeading>
              </Grid>
              <Grid item xs={12}>
                {!companyDetails?.TravelToHide &&
                  connectField("Travel1", {
                    label: messages?.timeCard?.updateTime?.travelTo,
                    disabled: isDNW,
                    CustomTimeField: showCustomTimeField,
                    minuteSteps: userMinutesStep,
                    disableDropDown: isDropdownDisabled,
                  })(TimeInput)}
              </Grid>
              <Grid item xs={12}>
                {!companyDetails?.TravelFromHide &&
                  connectField("Travel2", {
                    label: messages?.timeCard?.updateTime?.travelFrom,
                    disabled: isDNW,
                    CustomTimeField: showCustomTimeField,
                    minuteSteps: userMinutesStep,
                    disableDropDown: isDropdownDisabled,
                  })(TimeInput)}
              </Grid>
              <Grid item xs={12}>
                {connectField("Start", {
                  label: messages?.timeCard?.updateTime?.startWork,
                  disabled: isDNW,
                  CustomTimeField: showCustomTimeField,
                  minuteSteps: userMinutesStep,
                  disableDropDown: isDropdownDisabled,
                })(TimeInput)}
              </Grid>
              <Grid item xs={12}>
                {connectField("TotalMBDed", {
                  label: messages?.timeCard?.updateTime?.mealBreak,
                  disabled: isDNW || useMBValues || isLocked,
                  CustomTimeField: showCustomTimeField,
                  minuteSteps: userMinutesStep,
                  disableDropDown: isDropdownDisabled,
                })(TimeInput)}
              </Grid>

              {formValues?.Payas?.value?.id !== "DNW" && (
                <Grid item xs={12}>
                  <Accordion>
                    <StyledAccordionSummary
                      expandIcon={<ExpandMoreIcon style={expandIconStyles} />}
                      aria-controls="australia"
                      id="australia"
                    >
                      <FixedTitle
                        varient="Fixed14Medium"
                        color={brand?.secondaryMain}
                      >
                        {messages?.timeCard?.updateTime?.mbDetails}
                      </FixedTitle>
                    </StyledAccordionSummary>

                    <StyledAccordionDetails>
                      <OutlinedContainer
                        borderRadius="8px"
                        padding="16px"
                        borderColor={greyScaleColour?.grey70}
                      >
                        <FormRow>
                          <FixedTitle
                            varient="Fixed14Regular"
                            color={brand?.secondaryMain}
                          >
                            {messages?.timeCard?.updateTime?.mealBreak}
                          </FixedTitle>
                        </FormRow>
                        <Grid container xs={12} spacing={2}>
                          <Grid item xs={12}>
                            {connectField("StartMeal", {
                              label: `${messages?.timeCard?.updateTime?.startMB}1`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item xs={12}>
                            {connectField("FinishMeal1", {
                              label: `${messages?.timeCard?.updateTime?.finishMB}1`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item>
                            {connectField("MB1ND", {
                              label: messages?.timeCard?.updateTime?.nonDed1,
                              flexDirection: "row-reverse",
                            })(CustomCheckbox)}
                          </Grid>
                          <Grid item xs={12}>
                            {connectField("StartMeal2", {
                              label: `${messages?.timeCard?.updateTime?.startMB}2`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item xs={12}>
                            {connectField("FinishMeal2", {
                              label: `${messages?.timeCard?.updateTime?.finishMB}2`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item xs={12} display="flex" alignSelf="center">
                            {connectField("MB2ND", {
                              label: messages?.timeCard?.updateTime?.nonDed2,
                              flexDirection: "row-reverse",
                            })(CustomCheckbox)}
                          </Grid>
                          <Grid item xs={12}>
                            {connectField("StartMeal3", {
                              label: `${messages?.timeCard?.updateTime?.startMB}3`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item xs={12}>
                            {connectField("FinishMeal3", {
                              label: `${messages?.timeCard?.updateTime?.finishMB}3`,
                              CustomTimeField: showCustomTimeField,
                              minuteSteps: userMinutesStep,
                              disableDropDown: isDropdownDisabled,
                            })(TimeInput)}
                          </Grid>
                          <Grid item xs={12} display="flex" alignSelf="center">
                            {connectField("MB3ND", {
                              label: messages?.timeCard?.updateTime?.nonDed3,
                              flexDirection: "row-reverse",
                            })(CustomCheckbox)}
                          </Grid>
                        </Grid>
                      </OutlinedContainer>
                    </StyledAccordionDetails>
                  </Accordion>
                </Grid>
              )}
              <Grid item xs={12}>
                {connectField("Finish", {
                  label: messages?.timeCard?.updateTime?.finishWork,
                  disabled: isDNW,
                  CustomTimeField: showCustomTimeField,
                  minuteSteps: userMinutesStep,
                  disableDropDown: isDropdownDisabled,
                })(TimeInput)}
              </Grid>

              <Grid item>
                <FixedTitle varient="Fixed14Bold">
                  {messages?.timeCard?.updateTime?.totalHours} {getTotalHours()}
                </FixedTitle>
              </Grid>

              <Grid item xs={12}>
                {connectField("Note", {
                  label: messages?.timeCard?.updateTime?.note,
                  multiline: true,
                  rows: 3,
                  autoHeight: true,
                })(TextInput)}
              </Grid>
              {companyDetails?.CustomTimeFields?.map((field: CustomField) => {
                return field?.hide ? null : (
                  <Grid item xs={12}>
                    <CustomFieldComponent
                      field={field}
                      connectField={connectField}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {submitError && (
              <FormRow>
                <FormRowItem>
                  <FormError message={submitError} />
                </FormRowItem>
              </FormRow>
            )}
          </Grid>
          <OutlinedContainer
            noBorder
            borderRadius="0"
            padding="14px 24px"
            styles={{
              backgroundColor: greyScaleColour?.grey60,
              marginTop: "24px",
            }}
          >
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      buttonColor={brand?.black}
                      color="primary"
                      size="large"
                      onClick={handleGoBack}
                      label={messages?.general?.cancel}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      type="submit"
                      label={messages?.general?.update}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </OutlinedContainer>
        </Form>
      </Card>
    </Container>
  );
};

export default AddTimeCardForm;
